import { ReactNode } from 'react';

import {
  Badge,
  Box,
  Button,
  HStack,
  Header,
  Pressable,
  Text,
  VStack,
} from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import { GestureResponderEvent, Platform } from 'react-native';

import { useOfferExpiration } from 'hooks/loyalty/use-offer-expiration';
import { OfferFeedbackMessage } from 'pages/cart/cart-item/styled';
import { useLoyaltyContext } from 'state/loyalty';
import { primitive } from 'styles/constants/primitives';

type IOfferGroupItem = {
  name: string;
  groupId?: string;
  description?: string;
  image: ReactNode;
  onPress?: ((event?: GestureResponderEvent) => any) | null | undefined;
  isPartnershipOffer?: boolean;
  activateButtonLabel?: string;
  onPartnershipActivate?: ((event: GestureResponderEvent) => void) | null | undefined;
  isWalmartUser?: boolean;
  linkedButtonLabel?: string;
  endDate?: string;
};

const topStackedCardShadow =
  Platform.OS === 'web' || Platform.OS === 'android'
    ? {
        boxShadow: '1px -1px 2px 1px rgba(0,0,0,0.2)',
      }
    : {
        // iOS
        shadowColor: '#000000',
        shadowOffset: { width: 1, height: -1 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
      };

const bottomStackedCardShadow =
  Platform.OS === 'web' || Platform.OS === 'android'
    ? {
        boxShadow: '1px -1px 2px 1px rgba(0,0,0,0.1)',
      }
    : {
        // iOS
        shadowColor: '#000000',
        shadowOffset: { width: 1, height: -1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      };

const BottomStackedCard = ({
  isActive,
  isPressed,
  isFocused,
}: {
  isActive?: boolean;
  isPressed?: boolean;
  isFocused?: boolean;
}) => {
  return (
    <Box
      position="absolute"
      left={0}
      right={0}
      top={0}
      bottom={0}
      rounded={10}
      zIndex={0}
      pointerEvents="none"
      bg={isActive ? primitive.bk.$disabledNoOpacity : primitive.$white}
      marginLeft={1}
      style={bottomStackedCardShadow}
    >
      <Box
        bg={isPressed || isFocused ? 'token.background-focus' : 'transparent'}
        width="full"
        height="full"
      />
    </Box>
  );
};

const StackedCardContainer = ({
  children,
  isActive,
  isPressed,
  isFocused,
  padding,
}: {
  children: ReactNode;
  isActive?: boolean;
  isPressed?: boolean;
  isFocused?: boolean;
  padding?: string[];
}) => {
  return (
    <>
      <BottomStackedCard isActive={isActive} isPressed={isPressed} isFocused={isFocused} />
      <Box
        bg={isActive ? primitive.bk.$disabledNoOpacity : primitive.$white}
        rounded={10}
        borderWidth={0}
        borderColor={'transparent'}
        style={{ ...topStackedCardShadow, transform: [{ translateY: 6 }, { translateX: -6 }] }}
        marginLeft={1}
      >
        <VStack
          p={padding}
          pt={'$3'}
          borderTopRadius={10}
          borderBottomRadius={10}
          bg={isPressed || isFocused ? 'token.background-focus' : 'transparent'}
        >
          {children}
        </VStack>
      </Box>
    </>
  );
};

const OfferGroupItemHeader = ({
  name,
  isPartnershipOffer,
  isHovered,
}: {
  name: string;
  isPartnershipOffer?: boolean;
  isHovered?: boolean;
}) => {
  return (
    <Header
      variant="headerThree"
      color={isHovered && !isPartnershipOffer ? 'token.text-active' : 'token.text-default'}
      ellipsizeMode="tail"
      numberOfLines={2}
      mb={isPartnershipOffer ? '$1' : '$2'}
      accessibilityLevel={2}
    >
      {name}
    </Header>
  );
};

const OfferGroupItemDescription = ({ description }: { description?: string }) => {
  if (!description) {
    return null;
  }
  return (
    <Text fontSize={12} lineHeight={16} numberOfLines={2} ellipsizeMode="tail">
      {description}
    </Text>
  );
};

const OfferGroupItemExpiration = ({ expiration }: { expiration?: string }) => {
  if (!expiration) {
    return null;
  }
  return <OfferFeedbackMessage>{expiration}</OfferFeedbackMessage>;
};

const OfferGroupItemPartnershipAction = ({
  isPartnershipOffer,
  isWalmartUser,
  activateButtonLabel,
  onPartnershipActivate,
  linkedButtonLabel,
}: {
  isPartnershipOffer?: boolean;
  isWalmartUser?: boolean;
  activateButtonLabel?: string;
  onPartnershipActivate?: (event: GestureResponderEvent) => void;
  linkedButtonLabel?: string;
}) => {
  if (!isPartnershipOffer) {
    return null;
  }
  if (!isWalmartUser) {
    return (
      <Button
        size="sm"
        mt="$2"
        mb="$2"
        pb="$1"
        pt="$1"
        onPress={onPartnershipActivate}
        variant="outline"
      >
        {activateButtonLabel}
      </Button>
    );
  }
  return (
    <Badge mt="$2" variant="default-subtle" backgroundColor="#D1FAE5" iconName="checkFill">
      {linkedButtonLabel || 'Active'}
    </Badge>
  );
};

export const OfferGroupItem = ({
  name,
  description,
  groupId,
  image,
  onPress,
  isPartnershipOffer,
  activateButtonLabel,
  onPartnershipActivate,
  isWalmartUser,
  linkedButtonLabel,
  endDate,
  ...buttonProps
}: IOfferGroupItem) => {
  const { formatMessage } = useIntl();
  const offerGroupBadgeLabel = formatMessage({ id: 'offerGroupBadgeLabel' });
  const expiresAt = useOfferExpiration(endDate, undefined, false);
  const { offersCooldown } = useLoyaltyContext();
  return (
    <Pressable
      accessibilityRole="button"
      dd-action-name={name}
      onPress={!offersCooldown.isActive ? onPress : null}
      mb="$4"
      width="full"
      {...buttonProps}
    >
      {({ isHovered, isPressed, isFocused }) => {
        const padding = isPartnershipOffer ? ['$0', '$0'] : ['$3', '$4'];
        const pressed = Boolean(isPressed && onPress);

        return (
          <StackedCardContainer
            isActive={offersCooldown.isActive}
            isPressed={pressed}
            isFocused={isFocused}
            padding={padding}
          >
            <HStack mr="$2">
              {image}
              <VStack flexShrink={1} ml="$3" alignItems="flex-start" justifyContent="center">
                <OfferGroupItemHeader
                  name={name}
                  isPartnershipOffer={isPartnershipOffer}
                  isHovered={isHovered}
                />
                <OfferGroupItemDescription description={description} />
                <OfferGroupItemExpiration expiration={expiresAt} />
                <OfferGroupItemPartnershipAction
                  isPartnershipOffer={isPartnershipOffer}
                  isWalmartUser={isWalmartUser}
                  activateButtonLabel={activateButtonLabel}
                  onPartnershipActivate={onPartnershipActivate}
                  linkedButtonLabel={linkedButtonLabel}
                />
                <Badge variant="default-solid" iconName="offer" mt="$2" mr="$2">
                  {offerGroupBadgeLabel}
                </Badge>
              </VStack>
            </HStack>
          </StackedCardContainer>
        );
      }}
    </Pressable>
  );
};
